import React, { useState, useMemo, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogBody,
  DialogActions,
} from "@/components/ui/dialog/Dialog";
import { Button } from "@/components/ui/button/Button";
import SelectDropdown, {
  type SelectOption,
} from "@/components/ui/select-dropdown";
import DatePicker from "@/components/ui/date-picker";
import FormInput from "@/components/ui/form-input";
import {
  getLastWeekEndBusinessDay,
  getLastMonthEndBusinessDay,
  getLastQuarterEndBusinessDay,
  getLastYearEndBusinessDay,
} from "@/utils/datetime";
import {
  applyFilenameTemplate,
  getDefaultFilenameTemplate,
  FILENAME_TEMPLATE_TAGS,
  type FilenameTemplateContext,
} from "@/utils/formatters";
import { defaultAsAtDateForFilename } from "@/utils/reportAsAtDate";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { Tooltip } from "@/components/ui/tooltip/Tooltip";

type DateOption =
  | "latest"
  | "yesterday"
  | "last_week_end"
  | "last_month_end"
  | "last_quarter_end"
  | "last_year_end"
  | "custom";

export interface GenerateConfirmResult {
  asAtDate?: string;
  filenameTemplate: string;
}

interface GenerateConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (result: GenerateConfirmResult) => void;
  /** Report being exported */
  report: { name?: string; type?: string };
  /** Whether this is a batch export (multiple PDFs) */
  isBatchMode?: boolean;
  /** Number of reports in batch (for display) */
  batchCount?: number;
  /** Sample resource name for preview (first resource in batch or single resource) */
  sampleResourceName?: string;
  /** Initial as-at date (optional) */
  initialAsAtDate?: string;
  /** Whether to show date picker (some reports may hide it) */
  showDatePicker?: boolean;
  /** Whether to show filename template input (hidden for view mode) */
  showFilenameTemplate?: boolean;
  /** Dialog title override */
  title?: string;
  /** Confirm button text override */
  confirmButtonText?: string;
}

export function GenerateConfirmDialog({
  open,
  onClose,
  onConfirm,
  report,
  isBatchMode = false,
  batchCount = 1,
  sampleResourceName,
  initialAsAtDate,
  showDatePicker = true,
  showFilenameTemplate = true,
  title,
  confirmButtonText,
}: GenerateConfirmDialogProps) {
  const [dateOption, setDateOption] = useState<DateOption>(
    initialAsAtDate ? "custom" : "latest"
  );
  const [customDate, setCustomDate] = useState<string>(
    initialAsAtDate || new Date().toISOString().split("T")[0]
  );
  const [filenameTemplate, setFilenameTemplate] = useState<string>("");
  const [showTagHelp, setShowTagHelp] = useState(false);

  // Initialize filename template when dialog opens
  useEffect(() => {
    if (open) {
      const context: FilenameTemplateContext = {
        report_name: report.name,
        report_type: report.type,
        resource_name: sampleResourceName || undefined,
      };
      setFilenameTemplate(getDefaultFilenameTemplate(context));
    }
  }, [open, report.name, report.type, sampleResourceName]);

  // Reset date option when initialAsAtDate changes
  useEffect(() => {
    if (initialAsAtDate) {
      setDateOption("custom");
      setCustomDate(initialAsAtDate);
    }
  }, [initialAsAtDate]);

  const dateOptions: SelectOption<DateOption>[] = [
    { value: "latest", label: "Latest" },
    { value: "yesterday", label: "Yesterday" },
    { value: "last_week_end", label: "Last Week End" },
    { value: "last_month_end", label: "Last Month End" },
    { value: "last_quarter_end", label: "Last Quarter End" },
    { value: "last_year_end", label: "Last Year End" },
    { value: "custom", label: "Custom..." },
  ];

  const formatDateForDisplay = (dateString: string): string => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getOptionDisplayDate = (option: DateOption): string | null => {
    const now = new Date();
    switch (option) {
      case "latest":
        return formatDateForDisplay(now.toISOString().split("T")[0]);
      case "yesterday": {
        const yesterday = new Date(now);
        yesterday.setDate(now.getDate() - 1);
        return formatDateForDisplay(yesterday.toISOString().split("T")[0]);
      }
      case "last_week_end":
        return formatDateForDisplay(getLastWeekEndBusinessDay());
      case "last_month_end":
        return formatDateForDisplay(getLastMonthEndBusinessDay());
      case "last_quarter_end":
        return formatDateForDisplay(getLastQuarterEndBusinessDay());
      case "last_year_end":
        return formatDateForDisplay(getLastYearEndBusinessDay());
      case "custom":
        return formatDateForDisplay(customDate);
      default:
        return null;
    }
  };

  const getSelectedDate = (): string | undefined => {
    if (!showDatePicker || dateOption === "latest") {
      return undefined;
    }

    const now = new Date();
    switch (dateOption) {
      case "yesterday": {
        const yesterday = new Date(now);
        yesterday.setDate(now.getDate() - 1);
        return yesterday.toISOString().split("T")[0];
      }
      case "last_week_end":
        return getLastWeekEndBusinessDay();
      case "last_month_end":
        return getLastMonthEndBusinessDay();
      case "last_quarter_end":
        return getLastQuarterEndBusinessDay();
      case "last_year_end":
        return getLastYearEndBusinessDay();
      case "custom":
        return customDate;
      default:
        return undefined;
    }
  };

  const selectedAsAtDate = getSelectedDate();

  // Generate preview of the filename
  const filenamePreview = useMemo(() => {
    const selectedDate = selectedAsAtDate || defaultAsAtDateForFilename();
    const context: FilenameTemplateContext = {
      report_name: report.name,
      report_type: report.type,
      date: selectedDate,
      resource_name: sampleResourceName,
      index: isBatchMode ? 1 : undefined,
      total: isBatchMode ? batchCount : undefined,
    };
    const resolved = applyFilenameTemplate(filenameTemplate, context);
    return resolved + ".pdf";
  }, [filenameTemplate, report, sampleResourceName, isBatchMode, batchCount, dateOption, customDate, selectedAsAtDate, showDatePicker]);

  const handleConfirm = () => {
    onConfirm({
      asAtDate: selectedAsAtDate,
      filenameTemplate,
    });
  };

  const handleInsertTag = (tag: string) => {
    setFilenameTemplate((prev) => prev + tag);
  };

  // Determine dialog title
  const dialogTitle = title || (isBatchMode ? "Generate Batch Reports" : "Generate Report");
  
  // Determine confirm button text
  const buttonText = confirmButtonText || (isBatchMode 
    ? `Generate ${batchCount} Report${batchCount !== 1 ? "s" : ""}` 
    : "Generate");

  return (
    <Dialog open={open} onClose={onClose} size={showFilenameTemplate ? "lg" : "md"} className="!overflow-visible">
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogBody className="!overflow-visible">
        <div className="space-y-5">
          {/* Summary */}
          <div className="text-slate-600 dark:text-slate-300">
            {isBatchMode ? (
              <p>
                Generate {batchCount} report{batchCount !== 1 ? "s" : ""} from{" "}
                <span className="font-medium text-slate-800 dark:text-slate-200">
                  {report.name || "Report"}
                </span>
              </p>
            ) : (
              <p>
                Generate report from{" "}
                <span className="font-medium text-slate-800 dark:text-slate-200">
                  {report.name || "Report"}
                </span>
                {sampleResourceName && (
                  <>
                    {" "}for{" "}
                    <span className="font-medium text-slate-800 dark:text-slate-200">
                      {sampleResourceName}
                    </span>
                  </>
                )}
              </p>
            )}
          </div>

          {/* Data As At */}
          {showDatePicker && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                Data As At
              </label>
              <div className="relative">
                <SelectDropdown
                  options={dateOptions}
                  value={dateOption}
                  onChange={setDateOption}
                  usePortal
                  keepOpenOnSelect={(option) => option === "custom"}
                  buttonClassName="w-full text-sm py-2.5 px-3"
                  dropdownClassName="text-sm z-50"
                  dropdownWidth="w-full"
                  renderSelectedItem={(option) => {
                    if (option.value === "custom") {
                      return formatDateForDisplay(customDate);
                    }
                    return option.label;
                  }}
                  renderListItem={(option, isSelected, isFocused) => {
                    const displayDate = getOptionDisplayDate(option.value);
                    return (
                      <div className="py-2 pr-3 pl-3 flex justify-between items-center text-base">
                        <span
                          className={`block truncate font-normal ${isSelected ? "font-semibold" : ""}`}
                        >
                          {option.label}
                        </span>
                        {displayDate && (
                          <span
                            className={`text-xs font-mono ${isFocused ? "text-white/70" : "text-slate-500 dark:text-slate-400"}`}
                          >
                            {displayDate}
                          </span>
                        )}
                      </div>
                    );
                  }}
                  renderListFooter={() =>
                    dateOption === "custom" ? (
                      <div className="border-t border-slate-200 dark:border-slate-600 pt-3 mt-2">
                        <div className="px-1">
                          <DatePicker
                            value={customDate}
                            onChange={(val) =>
                              setCustomDate(
                                Array.isArray(val) ? val[0] || customDate : val
                              )
                            }
                            showPresets={false}
                            disableFutureDates={false}
                            disableCalendar={false}
                            dropdownPosition="auto"
                            usePortal={false}
                          />
                        </div>
                      </div>
                    ) : null
                  }
                />
              </div>
            </div>
          )}

          {/* Filename Template - only shown for generate/download actions */}
          {showFilenameTemplate && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  Filename Template
                </label>
                <Tooltip content="Use template tags to customize PDF filenames. Click the help icon to see available tags.">
                  <button
                    type="button"
                    onClick={() => setShowTagHelp(!showTagHelp)}
                    className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                  >
                    <FontAwesomeIcon icon={faInfoCircle} className="w-4 h-4" />
                  </button>
                </Tooltip>
              </div>
              
              <FormInput
                type="text"
                value={filenameTemplate}
                onChange={(e) => setFilenameTemplate(e.target.value)}
                placeholder="{report_type} - {date} - {resource_name}"
                className="font-mono text-sm"
              />

              {/* Preview */}
              <div className="text-xs text-slate-500 dark:text-slate-400">
                <span className="font-medium">Preview: </span>
                <span className="font-mono bg-slate-100 dark:bg-slate-700/50 px-1.5 py-0.5 rounded">
                  {filenamePreview}
                </span>
                {isBatchMode && batchCount > 1 && (
                  <span className="ml-2 text-slate-400">
                    (showing first of {batchCount})
                  </span>
                )}
              </div>

              {/* Tag Help */}
              {showTagHelp && (
                <div className="mt-3 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700">
                  <div className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-2">
                    Available Template Tags
                  </div>
                  <div className="grid grid-cols-1 gap-1.5">
                    {FILENAME_TEMPLATE_TAGS.map(({ tag, description, example }) => (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => handleInsertTag(tag)}
                        className="flex items-center gap-3 text-left text-xs hover:bg-slate-100 dark:hover:bg-slate-700/50 p-1.5 rounded transition-colors group"
                      >
                        <code className="font-mono text-indigo-600 dark:text-indigo-400 bg-slate-100 dark:bg-slate-700 px-1.5 py-0.5 rounded group-hover:bg-indigo-100 dark:group-hover:bg-indigo-900/30">
                          {tag}
                        </code>
                        <span className="text-slate-600 dark:text-slate-400 flex-1">
                          {description}
                        </span>
                        <span className="text-slate-400 dark:text-slate-500 font-mono text-[10px]">
                          {example}
                        </span>
                      </button>
                    ))}
                  </div>
                  <div className="mt-2 text-[10px] text-slate-400 dark:text-slate-500">
                    Click a tag to insert it at the end of the template
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogBody>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button color="indigo" onClick={handleConfirm}>
          {buttonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
